import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';

const AlertDetails = () => {
  const { alertId } = useParams();
  const navigate = useNavigate();
  const [alert, setAlert] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  // Define the API URL using process.env
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

  useEffect(() => {
    const fetchAlertDetail = async () => {
      if (!alertId) {
        setError('Invalid alert ID provided.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await axios.get(`${API_URL}/alerts/${alertId}`);
        setAlert(response.data);
        setError(null);
      } catch (err) {
        console.error("Error fetching alert:", err);
        if (err.response) {
          setError(`Failed to fetch alert: ${err.response.status} - ${err.response.data.message || err.message}`);
        } else if (err.request) {
          setError('No response from server. Please check the backend connection.');
        } else {
          setError(`An unexpected error occurred: ${err.message}`);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchAlertDetail();
  }, [alertId, API_URL]);

  const getSeverityConfig = (severity) => {
    switch (severity?.toLowerCase()) {
      case 'critical': 
        return {
          color: 'text-red-600',
          bg: 'bg-red-50',
          border: 'border-red-200',
          accent: 'bg-red-500',
          shadow: 'shadow-red-100',
          gradient: 'from-red-500 to-red-600'
        };
      case 'high': 
        return {
          color: 'text-orange-600',
          bg: 'bg-orange-50',
          border: 'border-orange-200',
          accent: 'bg-orange-500',
          shadow: 'shadow-orange-100',
          gradient: 'from-orange-500 to-orange-600'
        };
      case 'medium': 
        return {
          color: 'text-yellow-600',
          bg: 'bg-yellow-50',
          border: 'border-yellow-200',
          accent: 'bg-yellow-500',
          shadow: 'shadow-yellow-100',
          gradient: 'from-yellow-500 to-yellow-600'
        };
      case 'low': 
        return {
          color: 'text-green-600',
          bg: 'bg-green-50',
          border: 'border-green-200',
          accent: 'bg-green-500',
          shadow: 'shadow-green-100',
          gradient: 'from-green-500 to-green-600'
        };
      default: 
        return {
          color: 'text-gray-600',
          bg: 'bg-gray-50',
          border: 'border-gray-200',
          accent: 'bg-gray-500',
          shadow: 'shadow-gray-100',
          gradient: 'from-gray-500 to-gray-600'
        };
    }
  };

  const handleBackToAlerts = () => {
    // Try to navigate back to alerts page, fallback to browser back
    try {
      navigate('/');
    } catch (err) {
      console.error('Navigation failed, using browser back:', err);
      window.history.back();
    }
  };

  const handleDownloadPDF = async (pdfUrl) => {
    try {
      console.log('Downloading PDF from:', pdfUrl);
      
      // Create a temporary anchor element to trigger download
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = `${alert.name || 'alert'}.pdf`;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      
      // Append to body, click, and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      console.log('PDF download initiated');
    } catch (err) {
      console.error('Error downloading PDF:', err);
      // Fallback: open in new tab
      window.open(pdfUrl, '_blank', 'noopener,noreferrer');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center">
        <div className="text-center">
          <div className="relative w-20 h-20 mx-auto mb-8">
            <div className="absolute inset-0 rounded-full border-4 border-blue-100"></div>
            <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-blue-500 animate-spin"></div>
            <div className="absolute inset-2 rounded-full border-2 border-transparent border-t-indigo-400 animate-spin animate-reverse"></div>
          </div>
          <h2 className="text-3xl font-bold text-slate-800 mb-3">Loading Alert</h2>
          <p className="text-slate-600 text-lg">Fetching cybersecurity intelligence...</p>
          <div className="mt-6 flex justify-center">
            <div className="flex space-x-2">
              <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"></div>
              <div className="w-2 h-2 bg-indigo-500 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
              <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-rose-50 to-pink-50 flex items-center justify-center p-4">
        <div className="max-w-lg w-full bg-white rounded-3xl border border-red-200 p-10 text-center shadow-2xl shadow-red-100 animate-fade-in">
          <div className="w-20 h-20 mx-auto mb-8 bg-red-100 rounded-full flex items-center justify-center">
            <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center animate-pulse">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
          </div>
          <h2 className="text-3xl font-bold text-red-600 mb-4">System Alert</h2>
          <p className="text-slate-700 mb-8 leading-relaxed">{error}</p>
          <button
            onClick={handleBackToAlerts}
            className="px-8 py-4 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-bold rounded-xl hover:from-blue-600 hover:to-indigo-700 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl focus:outline-none focus:ring-4 focus:ring-blue-200"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (!alert) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-50 flex items-center justify-center p-4">
        <div className="max-w-lg w-full bg-white rounded-3xl border border-amber-200 p-10 text-center shadow-2xl shadow-amber-100 animate-fade-in">
          <div className="w-20 h-20 mx-auto mb-8 bg-amber-100 rounded-full flex items-center justify-center">
            <div className="w-16 h-16 bg-amber-500 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
            </div>
          </div>
          <h2 className="text-3xl font-bold text-amber-600 mb-4">Alert Not Found</h2>
          <p className="text-slate-700 mb-8">The requested security alert could not be located in our system.</p>
          <button
            onClick={handleBackToAlerts}
            className="px-8 py-4 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-bold rounded-xl hover:from-blue-600 hover:to-indigo-700 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl focus:outline-none focus:ring-4 focus:ring-blue-200"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const severityConfig = getSeverityConfig(alert.severity);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 text-slate-800 font-sans relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-200/30 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-2000"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-indigo-200/30 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-4000"></div>
        <div className="absolute top-1/2 -left-20 w-80 h-80 bg-purple-200/30 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob"></div>
        
        {/* Geometric Patterns */}
        <div className="absolute top-20 right-20 w-32 h-32 border border-blue-200/50 rotate-45 animate-pulse"></div>
        <div className="absolute bottom-40 left-40 w-24 h-24 border border-indigo-200/50 rotate-12 animate-bounce"></div>
        
        {/* Floating Particles */}
        <div className="absolute top-1/3 left-1/2 w-2 h-2 bg-blue-400 rounded-full animate-ping"></div>
        <div className="absolute top-2/3 left-1/4 w-3 h-3 bg-indigo-400 rounded-full animate-pulse"></div>
        <div className="absolute top-1/2 right-1/3 w-1 h-1 bg-purple-400 rounded-full animate-bounce"></div>
      </div>

      {/* Header Section */}
      <div className="relative z-10 bg-white/80 backdrop-blur-xl border-b border-slate-200/50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
          
          {/* Breadcrumb */}
          <div className="flex items-center gap-3 text-slate-500 mb-8 text-sm font-medium">
            <button
              onClick={handleBackToAlerts}
              className="flex items-center gap-2 hover:text-blue-600 transition-all duration-300 transform hover:translate-x-1 hover:bg-blue-50 px-3 py-2 rounded-lg"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Security Alerts
            </button>
            <div className="w-1 h-1 bg-slate-300 rounded-full"></div>
            <span className="text-blue-600 font-semibold">Threat Intelligence</span>
          </div>

          {/* Alert Header */}
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-8">
            <div className="flex-1">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <div>
                  <h1 className="text-4xl lg:text-5xl font-black text-slate-800 mb-2 leading-tight">
                    {alert.name}
                  </h1>
                  <p className="text-slate-600 text-lg">GLOCYBS Threat Intelligence Report</p>
                </div>
              </div>
              
              <div className="flex flex-wrap items-center gap-4 mb-8">
                <div className={`flex items-center gap-3 px-6 py-3 rounded-2xl ${severityConfig.bg} ${severityConfig.border} border-2 transform hover:scale-105 transition-all duration-300 shadow-lg ${severityConfig.shadow}`}>
                  <div className={`w-4 h-4 rounded-full ${severityConfig.accent} animate-pulse shadow-lg`} />
                  <span className={`text-lg font-bold ${severityConfig.color} uppercase tracking-wide`}>
                    {alert.severity} Priority
                  </span>
                </div>
                
                <div className="flex items-center gap-3 px-6 py-3 rounded-2xl bg-blue-50 border-2 border-blue-200 transform hover:scale-105 transition-all duration-300 shadow-lg shadow-blue-100">
                  <div className="w-4 h-4 bg-blue-500 rounded-full animate-pulse shadow-lg" />
                  <span className="text-lg font-bold text-blue-600 uppercase tracking-wide">
                    {alert.type}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-4 text-slate-600 mb-6">
                <div className="flex items-center gap-3 bg-white rounded-xl px-4 py-3 shadow-sm border border-slate-200">
                  <svg className="w-6 h-6 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span className="font-semibold text-lg">
                    Published: {new Date(alert.date).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </span>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col gap-4 lg:min-w-[280px]">
              <button
                onClick={handleBackToAlerts}
                className="flex items-center justify-center gap-3 px-8 py-4 bg-gradient-to-r from-slate-600 to-slate-700 text-white font-bold rounded-2xl hover:from-slate-700 hover:to-slate-800 transition-all duration-300 transform hover:scale-105 shadow-xl hover:shadow-2xl focus:outline-none focus:ring-4 focus:ring-slate-200"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back to Dashboard
              </button>
              
              {/* PDF Download Button */}
              {alert.pdfUrl && (
                <button
                  onClick={() => handleDownloadPDF(alert.pdfUrl)}
                  className="flex items-center justify-center gap-3 px-8 py-4 bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-bold rounded-2xl hover:from-emerald-600 hover:to-teal-700 transition-all duration-300 transform hover:scale-105 shadow-xl hover:shadow-2xl focus:outline-none focus:ring-4 focus:ring-emerald-200"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Download Report
                </button>
              )}
              
              {/* Debug: Show PDF URL status */}
              {process.env.NODE_ENV === 'development' && (
                <div className="text-xs text-slate-500 mt-2 p-3 bg-slate-100 rounded-lg">
                  PDF Status: {alert.pdfUrl ? '✅ Available' : '❌ Not Available'}
                  {alert.pdfUrl && <div className="break-all mt-1 font-mono text-slate-400">{alert.pdfUrl}</div>}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        <div className="space-y-12 lg:space-y-16">
          
          {/* Alert Overview */}
          {alert.description && (
            <div className="bg-white/90 backdrop-blur-xl rounded-3xl border border-slate-200/50 p-8 lg:p-12 shadow-2xl shadow-slate-200/50 hover:shadow-xl transition-all duration-500 animate-fade-in">
              <div className="flex items-start gap-6 mb-8">
                <div className="w-16 h-16 bg-gradient-to-br from-red-500 to-rose-600 rounded-2xl flex items-center justify-center transform rotate-3 shadow-xl">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-3xl lg:text-4xl font-bold text-slate-800 mb-3">Threat Overview</h2>
                  <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full"></div>
                </div>
              </div>
              <div className="bg-slate-50/80 rounded-2xl p-6 border-l-4 border-blue-500">
                <p className="text-slate-700 text-lg leading-relaxed font-medium">
                  {alert.description}
                </p>
              </div>
            </div>
          )}

          {/* CVE Information */}
          {alert.cve && (
            <div className={`bg-white/90 backdrop-blur-xl rounded-3xl border border-slate-200/50 p-8 lg:p-12 shadow-2xl shadow-slate-200/50 hover:shadow-xl transition-all duration-500 animate-fade-in delay-200`}>
              <div className="flex items-start gap-6 mb-8">
                <div className={`w-16 h-16 bg-gradient-to-br ${severityConfig.gradient} rounded-2xl flex items-center justify-center transform -rotate-3 shadow-xl`}>
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-3xl lg:text-4xl font-bold text-slate-800 mb-3">CVE Intelligence</h2>
                  <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full"></div>
                </div>
              </div>
              <div className="bg-slate-900 rounded-2xl p-6 border border-slate-200 shadow-inner">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
                  <span className="text-slate-400 text-sm font-mono uppercase tracking-wide">Vulnerability Identifier</span>
                </div>
                <p className="text-green-400 text-xl font-mono leading-relaxed break-all bg-slate-800 p-4 rounded-lg border border-slate-700">
                  {alert.cve}
                </p>
              </div>
            </div>
          )}

          {/* Background */}
          {alert.background && (
            <div className="bg-white/90 backdrop-blur-xl rounded-3xl border border-slate-200/50 p-8 lg:p-12 shadow-2xl shadow-slate-200/50 hover:shadow-xl transition-all duration-500 animate-fade-in delay-400">
              <div className="flex items-start gap-6 mb-8">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-2xl flex items-center justify-center transform rotate-6 shadow-xl">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-3xl lg:text-4xl font-bold text-slate-800 mb-3">Background Analysis</h2>
                  <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full"></div>
                </div>
              </div>
              <div className="bg-blue-50/80 rounded-2xl p-6 border-l-4 border-blue-500">
                <p className="text-slate-700 text-lg leading-relaxed font-medium">
                  {alert.background}
                </p>
              </div>
            </div>
          )}

          {/* Q&A Section */}
          {alert.questions && alert.questions.length > 0 && (
            <div className="bg-white/90 backdrop-blur-xl rounded-3xl border border-slate-200/50 p-8 lg:p-12 shadow-2xl shadow-slate-200/50 hover:shadow-xl transition-all duration-500 animate-fade-in delay-600">
              <div className="flex items-start gap-6 mb-10">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-2xl flex items-center justify-center transform -rotate-6 shadow-xl">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-3xl lg:text-4xl font-bold text-slate-800 mb-3">Critical Analysis</h2>
                  <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full"></div>
                </div>
              </div>
              
              <div className="space-y-8">
                {alert.questions.map((q, index) => (
                  <div key={index} className={`bg-gradient-to-r from-slate-50 to-blue-50 rounded-3xl p-8 lg:p-10 border-l-8 ${severityConfig.border} transform hover:scale-102 hover:shadow-xl transition-all duration-300 shadow-lg`}>
                    <div className="flex items-start gap-6">
                      <div className={`w-14 h-14 bg-gradient-to-br ${severityConfig.gradient} rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg transform hover:rotate-6 transition-transform duration-300`}>
                        <span className="text-xl font-black text-white">
                          {String(index + 1).padStart(2, '0')}
                        </span>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-2xl lg:text-3xl font-bold text-slate-800 mb-4 leading-snug">
                          {q.question}
                        </h3>
                        <div className="bg-white/80 rounded-xl p-6 border border-slate-200/50 shadow-inner">
                          <p className="text-slate-700 leading-relaxed text-lg font-medium">
                            {q.answer}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* GLOCYBS Branding Footer */}
          <div className="bg-gradient-to-r from-slate-800 via-slate-900 to-slate-800 rounded-3xl p-8 lg:p-12 shadow-2xl text-center">
            <div className="flex items-center justify-center gap-4 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-cyan-500 rounded-xl flex items-center justify-center shadow-lg">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-white">GLOCYBS</h3>
            </div>
            <p className="text-slate-300 text-lg mb-4 max-w-2xl mx-auto leading-relaxed">
              Where AI Thinks, Quantum Acts, and You Stay Secure
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-sm text-slate-400">
              <span className="bg-slate-700/50 px-4 py-2 rounded-full">BIQUA Intelligence</span>
              <span className="bg-slate-700/50 px-4 py-2 rounded-full">G-FLEA Analytics</span>
              <span className="bg-slate-700/50 px-4 py-2 rounded-full">QUBISA Security</span>
            </div>
          </div>

        </div>
      </div>

      {/* Custom Styles for Animations */}
      <style jsx>{`
        @keyframes blob {
          0% { transform: translate(0px, 0px) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
          100% { transform: translate(0px, 0px) scale(1); }
        }
        
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        @keyframes fade-in-up {
          from { opacity: 0; transform: translateY(40px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        @keyframes pulse-slow {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        
        .animate-blob {
          animation: blob 7s infinite;
        }
        
        .animate-fade-in {
          animation: fade-in 0.8s ease-out;
        }
        
        .animate-fade-in-up {
          animation: fade-in-up 1s ease-out;
        }
        
        .animate-pulse-slow {
          animation: pulse-slow 2s ease-in-out infinite;
        }
        
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        
        .animation-delay-4000 {
          animation-delay: 4s;
        }
        
        .delay-200 {
          animation-delay: 0.2s;
        }
        
        .delay-400 {
          animation-delay: 0.4s;
        }
        
        .delay-600 {
          animation-delay: 0.6s;
        }
        
        .hover\\:scale-102:hover {
          transform: scale(1.02);
        }
        
        .animate-reverse {
          animation-direction: reverse;
        }
      `}</style>
    </div>
  );
};

export default AlertDetails;